import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './ConnectorSettingsPage.css';

const CHANNEL_META = {
  dev: {
    label: 'Dev Channel',
    icon: '🧑‍💻',
    description: 'Developer communications — notes, bugs, progress tracking.',
    defaultLabel: 'Dev Space',
  },
  staff: {
    label: 'Staff Channel',
    icon: '👥',
    description: 'Staff collaboration and internal announcements.',
    defaultLabel: 'Staff Space',
  },
  citizen: {
    label: 'Citizen Channel',
    icon: '🏛️',
    description: 'Citizen-facing support and community communications.',
    defaultLabel: 'Citizen Space',
  },
};

const ALL_USER_TYPES = [
  { value: 'user',      label: 'User' },
  { value: 'citizen',   label: 'Citizen' },
  { value: 'staff',     label: 'Staff' },
  { value: 'education', label: 'Education' },
  { value: 'admin',     label: 'Admin' },
];

const EMPTY_CHANNELS = {
  dev:     { slug: '', label: '', enabled: false },
  staff:   { slug: '', label: '', enabled: false },
  citizen: { slug: '', label: '', enabled: false },
};

const createEmptyExternalSite = () => ({
  siteKey: '',
  siteName: '',
  origin: '',
  tenantSlug: 'public',
  accessKey: '',
  defaultUserType: 'user',
  allowAutoCreate: true,
  requireSignedPayload: true,
  enabled: false,
});

const ConnectorSettingsPage = () => {
  const { currentUser } = useContext(AuthContext);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [comsappBaseUrl, setComsappBaseUrl] = useState('');
  const [comsappEnabled, setComsappEnabled] = useState(true);
  const [urlOverride, setUrlOverride] = useState(false);
  const [defaultComsappBaseUrl, setDefaultComsappBaseUrl] = useState('');
  const [channels, setChannels] = useState(EMPTY_CHANNELS);
  const [allowedUserTypes, setAllowedUserTypes] = useState(ALL_USER_TYPES.map((t) => t.value));
  const [embedEnabled, setEmbedEnabled] = useState(false);
  const [cipIntegrationEnabled, setCipIntegrationEnabled] = useState(false);
  const [integrationMode, setIntegrationMode] = useState('testing');
  const [embedFrontendBaseUrl, setEmbedFrontendBaseUrl] = useState('');
  const [embedCipBaseUrl, setEmbedCipBaseUrl] = useState('');
  const [externalSites, setExternalSites] = useState([createEmptyExternalSite()]);

  // Per-channel slug verification state
  const [verifying, setVerifying] = useState({});
  const [verifyResult, setVerifyResult] = useState({});

  const fetchConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/connector/config');
      const cfg = res.data.config;
      setConfig(cfg);
      setDefaultComsappBaseUrl(res.data.defaultComsappBaseUrl || '');
      if (cfg) {
        setComsappBaseUrl(cfg.comsappBaseUrl || '');
        setComsappEnabled(cfg.comsappEnabled !== false);
        setUrlOverride(Boolean(cfg.comsappBaseUrl));
        setChannels({
          dev:     { slug: cfg.channels?.dev?.slug || '', label: cfg.channels?.dev?.label || '', enabled: cfg.channels?.dev?.enabled ?? false },
          staff:   { slug: cfg.channels?.staff?.slug || '', label: cfg.channels?.staff?.label || '', enabled: cfg.channels?.staff?.enabled ?? false },
          citizen: { slug: cfg.channels?.citizen?.slug || '', label: cfg.channels?.citizen?.label || '', enabled: cfg.channels?.citizen?.enabled ?? false },
        });
        setAllowedUserTypes(
          Array.isArray(cfg.allowedUserTypes) && cfg.allowedUserTypes.length > 0
            ? cfg.allowedUserTypes
            : ALL_USER_TYPES.map((t) => t.value)
        );

        const embedCfg = cfg.embedSettings || {};
        const loadedSites = Array.isArray(embedCfg.externalSites)
          ? embedCfg.externalSites.map((site) => ({
              ...createEmptyExternalSite(),
              ...site,
              accessKey: site.accessKey && site.accessKey !== '********' ? site.accessKey : '',
            }))
          : [];

        setEmbedEnabled(Boolean(embedCfg.enabled));
        setCipIntegrationEnabled(Boolean(embedCfg.cipIntegrationEnabled));
        setIntegrationMode(embedCfg.integrationMode === 'live' ? 'live' : 'testing');
        setEmbedFrontendBaseUrl(embedCfg.frontendBaseUrl || '');
        setEmbedCipBaseUrl(embedCfg.cipBaseUrl || '');
        setExternalSites(loadedSites.length > 0 ? loadedSites : [createEmptyExternalSite()]);
      } else {
        setComsappEnabled(true);
        setCipIntegrationEnabled(false);
        setIntegrationMode('testing');
      }
    } catch (err) {
      setError('Failed to load connector config.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleChannelChange = (ch, field, value) => {
    setChannels((prev) => ({
      ...prev,
      [ch]: { ...prev[ch], [field]: value },
    }));
    // Reset verification when slug changes
    if (field === 'slug') {
      setVerifyResult((prev) => ({ ...prev, [ch]: null }));
    }
  };

  const handleVerifySlug = async (ch) => {
    const rawInput = channels[ch]?.slug?.trim();
    if (!rawInput) return;

    setVerifying((prev) => ({ ...prev, [ch]: true }));
    setVerifyResult((prev) => ({ ...prev, [ch]: null }));
    setError('');

    // Detect MongoDB ObjectId (24-char hex) — paste from URL or Space Info panel
    const isObjectId = /^[0-9a-f]{24}$/i.test(rawInput);

    try {
      const endpoint = isObjectId ? '/api/connector/verify-id' : '/api/connector/verify-slug';
      const payload = isObjectId ? { id: rawInput, channel: ch } : { slug: rawInput, channel: ch };
      const res = await api.post(endpoint, payload);
      setVerifyResult((prev) => ({ ...prev, [ch]: { ok: true, space: res.data.space } }));
      // If user pasted an ID, update the displayed slug to the resolved slug
      if (isObjectId && res.data.space?.slug) {
        setChannels((prev) => ({ ...prev, [ch]: { ...prev[ch], slug: res.data.space.slug } }));
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Verification failed.';
      setVerifyResult((prev) => ({ ...prev, [ch]: { ok: false, message: msg } }));
    } finally {
      setVerifying((prev) => ({ ...prev, [ch]: false }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/api/connector/config', {
        comsappBaseUrl: urlOverride ? comsappBaseUrl : '',
        comsappEnabled,
        channels,
        allowedUserTypes,
        embedSettings: {
          enabled: embedEnabled,
          cipIntegrationEnabled,
          integrationMode,
          frontendBaseUrl: embedFrontendBaseUrl,
          cipBaseUrl: embedCipBaseUrl,
          externalSites,
        },
      });
      setSuccess('Integrations saved.');
      await fetchConfig();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const updateExternalSite = (index, field, value) => {
    setExternalSites((prev) => prev.map((site, i) => (i === index ? { ...site, [field]: value } : site)));
  };

  const addExternalSite = () => {
    setExternalSites((prev) => [...prev, createEmptyExternalSite()]);
  };

  const removeExternalSite = (index) => {
    setExternalSites((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [createEmptyExternalSite()];
    });
  };

  const getSuggestedWordPressSnippet = (site) => {
    const apiBase = window.location.origin;
    const includeSignature = site.requireSignedPayload !== false;
    const signatureLines = includeSignature
      ? [
          '$timestamp = (string) round(microtime(true) * 1000);',
          '$nonce = wp_generate_uuid4();',
          '$next = "/harvesting";',
          '$signaturePayload = strtolower("' + (site.siteKey || 'your-site-key') + '") . "|" . strtolower(wp_get_current_user()->user_email) . "|" . $timestamp . "|" . $nonce . "|" . $next;',
          '$signature = hash_hmac("sha256", $signaturePayload, "' + (site.accessKey || 'server-side-secret') + '");',
        ]
      : [
          '$next = "/harvesting";',
        ];

    const bodySignatureLines = includeSignature
      ? [
          '    "timestamp" => $timestamp,',
          '    "nonce" => $nonce,',
          '    "signature" => $signature,',
        ]
      : [];

    return [
      ...signatureLines,
      '$response = wp_remote_post("' + apiBase + '/api/connector/public/embed-login", [',
      '  "timeout" => 12,',
      '  "body" => [',
      '    "siteKey" => "' + (site.siteKey || 'your-site-key') + '",',
      '    "accessKey" => "' + (site.accessKey || 'server-side-secret') + '",',
      '    "email" => wp_get_current_user()->user_email,',
      '    "firstName" => wp_get_current_user()->user_firstname,',
      '    "lastName" => wp_get_current_user()->user_lastname,',
      '    "next" => $next,',
      ...bodySignatureLines,
      '    "redirect" => "0"',
      '  ]',
      ']);',
      '$body = json_decode(wp_remote_retrieve_body($response), true);',
      'if (!empty($body["redirectUrl"])) {',
      '  wp_redirect($body["redirectUrl"]);',
      '  exit;',
      '}',
    ].join('\n');
  };

  if (loading) return <div className="connector-page"><p>Loading…</p></div>;

  return (
    <div className="connector-page">
      <div className="connector-header">
        <div>
          <h1>Integrations</h1>
          <p>Manage ComSapp channel routing and CIP/public embed cross-login from one place.</p>
        </div>
        <Link to="/admin-dashboard" className="connector-back-link">← Admin Dashboard</Link>
      </div>

      <form onSubmit={handleSave}>
        <section className="connector-section">
          <div className="connector-section-header">
            <h2>ComSapp</h2>
            <p>Configure channel routing, provisioning access rules, and ComSapp space linkage.</p>
          </div>

          <div className="connector-card">
            <h3>ComSapp Master Switch</h3>
            <p className="connector-hint">Turn ComSapp access on or off across RS (header Coms button, Coms Hub, and provisioning).</p>
            <label className="connector-toggle">
              <input
                type="checkbox"
                checked={comsappEnabled}
                onChange={(e) => setComsappEnabled(e.target.checked)}
              />
              <span className="connector-toggle-label">{comsappEnabled ? 'ComSapp Enabled' : 'ComSapp Disabled'}</span>
            </label>
          </div>

          <div className="connector-card">
            <h3>ComSapp Base URL</h3>
            <p className="connector-hint">Where ComSapp is running. Defaults to <code>{defaultComsappBaseUrl || '(not set)'}</code> — override only if you need a custom URL.</p>
            <label className="connector-toggle" style={{ marginBottom: urlOverride ? '0.75rem' : 0 }}>
              <input
                type="checkbox"
                checked={urlOverride}
                onChange={(e) => {
                  setUrlOverride(e.target.checked);
                  if (!e.target.checked) setComsappBaseUrl('');
                }}
              />
              <span className="connector-toggle-label">Override default URL</span>
            </label>
            {urlOverride && (
              <input
                type="text"
                className="connector-input"
                value={comsappBaseUrl}
                onChange={(e) => setComsappBaseUrl(e.target.value)}
                placeholder="https://comms.yourorg.ca"
                autoFocus
              />
            )}
            {!urlOverride && defaultComsappBaseUrl && (
              <p className="connector-default-url">Using default: <code>{defaultComsappBaseUrl}</code></p>
            )}
          </div>

        <div className="connector-card">
          <h3>ComSapp Access — User Types</h3>
          <p className="connector-hint">Select which user types are allowed to access ComSapp through this connector. Unchecked types will not be provisioned or given access.</p>
          <div className="connector-user-type-list">
            {ALL_USER_TYPES.map(({ value, label }) => (
              <label key={value} className="connector-toggle" style={{ marginBottom: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={allowedUserTypes.includes(value)}
                  onChange={(e) => {
                    setAllowedUserTypes((prev) =>
                      e.target.checked ? [...prev, value] : prev.filter((t) => t !== value)
                    );
                  }}
                />
                <span className="connector-toggle-label">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {Object.entries(CHANNEL_META).map(([ch, meta]) => {
          const result = verifyResult[ch];
          const channelCfg = config?.channels?.[ch];
          return (
            <div className="connector-card" key={ch}>
              <div className="connector-channel-header">
                <h3>{meta.icon} {meta.label}</h3>
                <label className="connector-toggle">
                  <input
                    type="checkbox"
                    checked={channels[ch]?.enabled ?? false}
                    onChange={(e) => handleChannelChange(ch, 'enabled', e.target.checked)}
                  />
                  <span className="connector-toggle-label">
                    {channels[ch]?.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
              <p className="connector-hint">{meta.description}</p>

              <div className="connector-field-row">
                <div className="connector-field">
                  <label>Space Slug or ID</label>
                  <div className="connector-slug-row">
                    <input
                      type="text"
                      className="connector-input"
                      value={channels[ch]?.slug || ''}
                      onChange={(e) => handleChannelChange(ch, 'slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      onPaste={(e) => {
                        // Allow raw paste (including ObjectIds with hex chars) before the onChange filter
                        const pasted = e.clipboardData.getData('text').trim();
                        if (/^[0-9a-f]{24}$/i.test(pasted)) {
                          e.preventDefault();
                          handleChannelChange(ch, 'slug', pasted.toLowerCase());
                        }
                      }}
                      placeholder={`slug (e.g. rs-dev) or space ID`}
                    />
                    <button
                      type="button"
                      className="connector-verify-btn"
                      disabled={!channels[ch]?.slug || verifying[ch]}
                      onClick={() => handleVerifySlug(ch)}
                    >
                      {verifying[ch] ? 'Verifying…' : 'Verify'}
                    </button>
                  </div>

                  {result?.ok && (
                    <div className="connector-verify-ok">
                      ✓ Connected to <strong>{result.space.name}</strong>
                      {result.space.isPrivate && ' (private)'}
                      {comsappBaseUrl && (
                        <a
                          href={`${comsappBaseUrl.replace(/\/$/, '')}/spaces/${result.space.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="connector-open-link"
                        >
                          Open in ComSapp
                        </a>
                      )}
                    </div>
                  )}
                  {result?.ok === false && (
                    <div className="connector-verify-fail">✗ {result.message}</div>
                  )}
                  {!result && channelCfg?.lastVerifiedAt && channelCfg?.spaceName && (
                    <div className="connector-verify-cached">
                      Last verified: <strong>{channelCfg.spaceName}</strong>{' '}
                      <span className="connector-hint-inline">
                        ({new Date(channelCfg.lastVerifiedAt).toLocaleDateString()})
                      </span>
                      {channelCfg.spaceId && (
                        <span className="connector-space-id"> · ID: <code>{channelCfg.spaceId}</code></span>
                      )}
                    </div>
                  )}
                </div>

                <div className="connector-field">
                  <label>Channel Label</label>
                  <input
                    type="text"
                    className="connector-input"
                    value={channels[ch]?.label || ''}
                    onChange={(e) => handleChannelChange(ch, 'label', e.target.value)}
                    placeholder={meta.defaultLabel}
                    maxLength={60}
                  />
                </div>
              </div>
            </div>
          );
        })}

        <div className="connector-card connector-info-card">
          <h3>How to connect an app</h3>
          <ol className="connector-steps">
            <li>Create a space in ComSapp and note its slug or ID (visible in the Space Members tab if you are owner/admin).</li>
            <li>Enter the slug (e.g. <code>rs-dev</code>) or paste the Space ID directly into the field above, then click <strong>Verify</strong>.</li>
            <li>Enable the channel and save settings.</li>
            <li>Set <code>COMSAPP_API_URL</code> and <code>COMSAPP_APP_KEY</code> in your backend <code>.env</code> file to enable live provisioning.</li>
            <li>Repeat this pattern in any connected app (CIP, KOYoga) using the same ComSapp space slugs.</li>
          </ol>
          <p className="connector-hint">Space slugs are unique across all ComSapp spaces and serve as the universal link identifier.</p>
        </div>
        </section>

        <section className="connector-section connector-section-cip">
          <div className="connector-section-header">
            <h2>CIP / Public Embed</h2>
            <p>Configure trusted external sites and cross-login handoff for CIP and partner portals.</p>
          </div>

        <div className="connector-card">
          <h3>External Site Embed + Cross Login</h3>
          <p className="connector-hint">
            Configure trusted partner/tenant sites (including WordPress) that can embed RSMIN components and perform cross-login.
          </p>

          <div className="connector-field-row" style={{ marginBottom: '0.75rem' }}>
            <div className="connector-field">
              <label>Link Mode</label>
              <select
                className="connector-input"
                value={integrationMode}
                onChange={(e) => setIntegrationMode(e.target.value)}
              >
                <option value="testing">Testing</option>
                <option value="live">Live</option>
              </select>
            </div>
            <div className="connector-field">
              <label>CIP Harvest Embed in RS</label>
              <label className="connector-toggle" style={{ marginTop: '0.45rem' }}>
                <input
                  type="checkbox"
                  checked={cipIntegrationEnabled}
                  onChange={(e) => setCipIntegrationEnabled(e.target.checked)}
                />
                <span className="connector-toggle-label">{cipIntegrationEnabled ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>
          </div>

          <p className="connector-hint">
            {integrationMode === 'testing'
              ? 'Testing mode is active. Use this while validating site linking and harvest embed behavior.'
              : 'Live mode is active. Use this for production partner handoff.'}
          </p>

          <label className="connector-toggle" style={{ marginBottom: '0.75rem' }}>
            <input
              type="checkbox"
              checked={embedEnabled}
              onChange={(e) => setEmbedEnabled(e.target.checked)}
            />
            <span className="connector-toggle-label">Enable public embed integration</span>
          </label>

          <div className="connector-field-row">
            <div className="connector-field">
              <label>Frontend Base URL (optional)</label>
              <input
                type="text"
                className="connector-input"
                value={embedFrontendBaseUrl}
                onChange={(e) => setEmbedFrontendBaseUrl(e.target.value)}
                placeholder="https://your-rsmin-site.ca"
              />
            </div>
            <div className="connector-field">
              <label>CIP Base URL (optional)</label>
              <input
                type="text"
                className="connector-input"
                value={embedCipBaseUrl}
                onChange={(e) => setEmbedCipBaseUrl(e.target.value)}
                placeholder="https://cip.yourdomain.ca"
              />
            </div>
          </div>

          {externalSites.map((site, index) => (
            <div key={`site-${index}`} className="connector-card" style={{ marginTop: '0.75rem' }}>
              <div className="connector-channel-header">
                <h3 style={{ margin: 0 }}>External Site #{index + 1}</h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <label className="connector-toggle" style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(site.enabled)}
                      onChange={(e) => updateExternalSite(index, 'enabled', e.target.checked)}
                    />
                    <span className="connector-toggle-label">Enabled</span>
                  </label>
                  <button type="button" className="connector-verify-btn" onClick={() => removeExternalSite(index)}>
                    Remove
                  </button>
                </div>
              </div>

              <div className="connector-field-row">
                <div className="connector-field">
                  <label>Site Key</label>
                  <input
                    type="text"
                    className="connector-input"
                    value={site.siteKey}
                    onChange={(e) => updateExternalSite(index, 'siteKey', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    placeholder="e.g. wp-rsmin-east"
                  />
                </div>
                <div className="connector-field">
                  <label>Site Name</label>
                  <input
                    type="text"
                    className="connector-input"
                    value={site.siteName}
                    onChange={(e) => updateExternalSite(index, 'siteName', e.target.value)}
                    placeholder="Client WordPress Site"
                  />
                </div>
              </div>

              <div className="connector-field-row">
                <div className="connector-field">
                  <label>Origin (optional but recommended)</label>
                  <input
                    type="text"
                    className="connector-input"
                    value={site.origin}
                    onChange={(e) => updateExternalSite(index, 'origin', e.target.value)}
                    placeholder="https://partner-site.ca"
                  />
                </div>
                <div className="connector-field">
                  <label>Tenant Slug</label>
                  <input
                    type="text"
                    className="connector-input"
                    value={site.tenantSlug}
                    onChange={(e) => updateExternalSite(index, 'tenantSlug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    placeholder="public"
                  />
                </div>
              </div>

              <div className="connector-field-row">
                <div className="connector-field">
                  <label>Access Key (shared secret)</label>
                  <input
                    type="text"
                    className="connector-input"
                    value={site.accessKey}
                    onChange={(e) => updateExternalSite(index, 'accessKey', e.target.value)}
                    placeholder="Set a server-side secret"
                  />
                </div>
                <div className="connector-field">
                  <label>Default User Type</label>
                  <select
                    className="connector-input"
                    value={site.defaultUserType || 'user'}
                    onChange={(e) => updateExternalSite(index, 'defaultUserType', e.target.value)}
                  >
                    <option value="user">User</option>
                    <option value="citizen">Citizen</option>
                  </select>
                </div>
              </div>

              <label className="connector-toggle" style={{ marginTop: '0.25rem' }}>
                <input
                  type="checkbox"
                  checked={Boolean(site.allowAutoCreate)}
                  onChange={(e) => updateExternalSite(index, 'allowAutoCreate', e.target.checked)}
                />
                <span className="connector-toggle-label">Allow auto-create RS account when user does not exist</span>
              </label>

              <label className="connector-toggle" style={{ marginTop: '0.25rem' }}>
                <input
                  type="checkbox"
                  checked={site.requireSignedPayload !== false}
                  onChange={(e) => updateExternalSite(index, 'requireSignedPayload', e.target.checked)}
                />
                <span className="connector-toggle-label">Require signed payload (HMAC + timestamp + nonce)</span>
              </label>

              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Integration endpoints</label>
                <textarea
                  className="connector-input"
                  rows={3}
                  readOnly
                  value={[
                    `Manifest: ${window.location.origin}/api/connector/public/embed-manifest?siteKey=${site.siteKey || 'your-site-key'}`,
                    `Login: ${window.location.origin}/api/connector/public/embed-login`,
                  ].join('\n')}
                />
              </div>

              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>WordPress server-side snippet</label>
                <textarea
                  className="connector-input"
                  rows={12}
                  readOnly
                  value={getSuggestedWordPressSnippet(site)}
                />
              </div>
            </div>
          ))}

          <div style={{ marginTop: '0.75rem' }}>
            <button type="button" className="connector-verify-btn" onClick={addExternalSite}>
              + Add External Site
            </button>
          </div>
        </div>
        </section>

        {error && <p className="connector-error">{error}</p>}
        {success && <p className="connector-success">{success}</p>}

        <div className="connector-actions">
          <button type="submit" className="connector-save-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save Integrations'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConnectorSettingsPage;
